// useAiCreditGating — Hook som hanterar pre-flight / reserve / commit /
// release av AI-credits runt en AI-request. Integrerar med
// aiStagingSession för preview-temp-layer + creditCostEstimator för
// uppskattning.
//
// Lifecycle:
//   preflightAndReserve({ estimatedAiCredits, correlationId })
//     → { ok: true } | { ok: false, reason }
//   commitReservation({ correlationId, actualCredits })
//     → { ok: true, refunded } | { ok: false }
//   releaseReservation({ correlationId })
//     → { ok: true, released } | { ok: false }
//
// Alla anrop är idempotenta via correlationId — samma id ger identiskt
// resultat vid retry.

import { useCallback } from 'react';
import { callRpc } from '@/lib/supabaseTyped';
import { toast } from '@/hooks/use-toast';
import { useCreditBalance } from '@/hooks/useCreditBalance';
import type { CreditKind } from '@/lib/creditTypes';

export interface PreflightInput {
  kind?: CreditKind;
  estimatedAiCredits: number;
  correlationId: string;
  ttlSeconds?: number;
}

export interface PreflightResult {
  ok: boolean;
  reason?: string;
  balance?: number;
  required?: number;
}

export interface CommitInput {
  correlationId: string;
  actualCredits: number;
}

export interface CommitResult {
  ok: boolean;
  refunded?: number;
  error?: string;
}

export interface ReleaseInput {
  correlationId: string;
}

export interface ReleaseResult {
  ok: boolean;
  released?: number;
  error?: string;
}

export function useAiCreditGating() {
  const { refetch } = useCreditBalance();

  const preflightAndReserve = useCallback(
    async (input: PreflightInput): Promise<PreflightResult> => {
      const kind = input.kind ?? 'ai';
      const { data, error } = await callRpc<{
        success: boolean; error?: string;
        balance?: number; required?: number;
        reservation_id?: string;
      }>('reserve_credits', {
        credit_kind: kind,
        reserve_amount: input.estimatedAiCredits,
        corr_id: input.correlationId,
        ttl_seconds: input.ttlSeconds ?? 300,
      });

      if (error) {
        toast({ title: 'Credit reservation failed', description: error.message, variant: 'destructive' });
        return { ok: false, reason: error.message };
      }
      if (!data?.success) {
        if (data?.error === 'insufficient-balance') {
          return {
            ok: false,
            reason: 'insufficient-balance',
            balance: data.balance ?? 0,
            required: data.required ?? input.estimatedAiCredits,
          };
        }
        return { ok: false, reason: data?.error ?? 'no-response' };
      }

      refetch();
      return { ok: true };
    },
    [refetch],
  );

  const commitReservation = useCallback(
    async (input: CommitInput): Promise<CommitResult> => {
      const { data, error } = await callRpc<{
        success: boolean; error?: string;
        consumed?: number; refunded?: number; tx_id?: string;
      }>('commit_credit_reservation', {
        corr_id: input.correlationId,
        actual_amount: input.actualCredits,
      });

      if (error) {
        toast({ title: 'Commit failed', description: error.message, variant: 'destructive' });
        return { ok: false, error: error.message };
      }
      if (!data?.success) {
        toast({ title: 'Commit rejected', description: data?.error, variant: 'destructive' });
        return { ok: false, error: data?.error };
      }

      refetch();
      if (data.refunded && data.refunded > 0) {
        toast({ title: `Used ${data.consumed} credits`, description: `(${data.refunded} refunded)` });
      } else {
        toast({ title: `Used ${data.consumed ?? input.actualCredits} credits` });
      }
      return { ok: true, refunded: data.refunded ?? 0 };
    },
    [refetch],
  );

  const releaseReservation = useCallback(
    async (input: ReleaseInput): Promise<ReleaseResult> => {
      const { data, error } = await callRpc<{ success: boolean; error?: string; released?: number }>(
        'release_credit_reservation',
        { corr_id: input.correlationId },
      );

      if (error) return { ok: false, error: error.message };
      if (!data?.success) return { ok: false, error: data?.error };

      refetch();
      return { ok: true, released: data.released ?? 0 };
    },
    [refetch],
  );

  return {
    preflightAndReserve,
    commitReservation,
    releaseReservation,
  };
}

export function makeCorrelationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `ai-${crypto.randomUUID()}`;
  return `ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
